import { useState } from 'react';
import { Link } from 'react-router-dom';
import { customPayRunGateway } from '../index';
import { ApiError } from '../../identity/types';
import { APPROVAL_NOT_MONEY_MOVEMENT_NOTE } from '../../payroll-review/reviewCopy';
import type { FinalizeForPaymentResponse } from '../types';
import type { StepProps } from './CustomPayRunWizard';
import { ExportCsvButton } from './ExportCsvButton';
import { PayrollRecordsTable } from './PayrollRecordsTable';
import { RunSummary } from './RunSummary';
import { usePayrollRecords } from './usePayrollRecords';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

/** Step 4 — Approved Payroll: the approved run's records. The step is only reachable once Payroll Review has approved the
 * run. "Pay Employees" is the existing SIMULATED finalize (EPN-191): it hands the approved run to Payment Processing's
 * simulated (Mock) provider and the server refuses anything else (not approved, provider not a Mock, Production). No money
 * moves, no direct deposit is sent, and no tax deposit or filing is made — and every server refusal is shown, never a fake
 * success. Whether the viewer may pay is the server's decision (403 is shown as such). */
export function ApprovedPayrollStep({ run, goToStep }: StepProps) {
  const { loading, error, records } = usePayrollRecords(run.id, true);
  const [search, setSearch] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [finalized, setFinalized] = useState<FinalizeForPaymentResponse | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  async function handlePayEmployees() {
    setFinalizing(true);
    setFinalizeError(null);
    try {
      setFinalized(await customPayRunGateway.finalizeForPayment(run.id));
    } catch (err) {
      setFinalized(null);
      // Production (and any build that blocks it) answers 501 with a stable code; show it verbatim.
      setFinalizeError(
        err instanceof ApiError && err.status === 501
          ? `${err.code ?? 'FEATURE_NOT_AVAILABLE_IN_STAGING'}: ${err.message}`
          : friendlyErrorMessage(err, 'Could not finalize this run for simulated payment.'),
      );
    } finally {
      setFinalizing(false);
    }
  }

  return (
    <div>
      <RunSummary run={run}>
        <div className="cpr-summary__tools">
          <input
            className="search-box"
            type="search"
            placeholder="Search in Table"
            aria-label="Search in table"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ExportCsvButton runId={run.id} />
        </div>
      </RunSummary>

      <p className="muted">This run was approved in Payroll Review. {APPROVAL_NOT_MONEY_MOVEMENT_NOTE}</p>
      {loading && <p className="muted">Loading…</p>}
      {error && (
        <p className="cpr-notice cpr-notice--error" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && <PayrollRecordsTable records={records} search={search} showSelection={false} emptyMessage="No approved payroll records for this run." />}

      <h4>
        Finalize (simulated payment) <span className="badge badge--caution">SIMULATION — no money moves</span>
      </h4>
      <p className="muted">
        Pay Employees hands the approved run to the simulated (Mock) payment provider so you can walk through the payment flow. No money
        moves, no direct deposit is sent, and no tax deposit or filing is made. It works only after the run is approved in Payroll Review,
        and only when the payment provider is a simulation.
      </p>
      {finalizeError && (
        <p className="cpr-notice cpr-notice--error" role="alert">
          {finalizeError}
        </p>
      )}
      {finalized && (
        <div className="cpr-notice cpr-notice--success" role="status">
          <p>
            {finalized.alreadyFinalized
              ? 'This run was already handed to simulated payment — showing the existing batch.'
              : 'Handed to simulated payment.'}{' '}
            Batch <code>{finalized.paymentBatchId}</code> is <strong>{finalized.paymentBatchStatus}</strong> (simulation — no money moved).
          </p>
          <p>
            <Link to="/payments">View in Payments</Link>
          </p>
        </div>
      )}

      <div className="cpr-footer">
        <button type="button" className="cpr-btn cpr-btn--outline" onClick={() => goToStep('custom-payroll')}>
          Back to Custom Payroll
        </button>
        <button type="button" className="cpr-btn cpr-btn--green" disabled={finalizing} onClick={() => void handlePayEmployees()}>
          {finalizing ? 'Finalizing (simulated)…' : 'Pay Employees (simulated)'}
        </button>
      </div>
    </div>
  );
}
